import React from 'react';
import { useQBStore } from '@/store/qbStore';
import { useLookupStore } from '@/store/lookupStore';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";

export const MetadataEditor: React.FC = () => {
  const { commonMetadata, updateCommonMetadata } = useQBStore();

  // Lookup store
  const {
    stages, grades, subjects, examTypes, competitions,
    fetchAll, fetchGradesByStage,
  } = useLookupStore()

  // 初始化加载 lookup 数据
  React.useEffect(() => {
    fetchAll()
  }, [fetchAll])

  // 学段变化时加载年级
  React.useEffect(() => {
    if (commonMetadata.stage) {
      fetchGradesByStage(commonMetadata.stage)
    }
  }, [commonMetadata.stage, fetchGradesByStage])

  // 当前学段可用的学科和年级
  const currentSubjects = subjects.filter(s => s.type === 'regular')
  const currentGrades = grades.filter(g => g.stageCode === commonMetadata.stage)

  const examTypeValue = commonMetadata.exam_type || '';
  const allPredefinedTypes = examTypes.map(t => t.code)
  const isOtherExamType =
    examTypeValue === 'other' || (!!examTypeValue && !allPredefinedTypes.includes(examTypeValue));
  const selectValue = isOtherExamType ? 'other' : examTypeValue;

  return (
    <Card className="bg-white shadow-sm border">
      <CardContent className="p-4 space-y-3">
        {/* 试卷名称 */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-500">试卷名称</label>
          <Input
            className="h-8 text-sm"
            placeholder="如：2024年北京市海淀区期末考试数学试卷"
            value={commonMetadata.name || ''}
            onChange={(e) => updateCommonMetadata({ name: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-7 gap-4">
          {/* 学段 */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500">学段</label>
            <Select
              value={commonMetadata.stage}
              onValueChange={(v) => {
                updateCommonMetadata({ stage: v, subject: '', grade: '' });
              }}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="选择学段" />
              </SelectTrigger>
              <SelectContent>
                {stages.filter(s => s.code !== '大学').map((s) => (
                  <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 学科 */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500">学科</label>
            <Select
              value={commonMetadata.subject}
              onValueChange={(v) => updateCommonMetadata({ subject: v })}
              disabled={!commonMetadata.stage}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder={commonMetadata.stage ? "选择学科" : "先选学段"} />
              </SelectTrigger>
              <SelectContent>
                {currentSubjects.map((s) => (
                  <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 年级 */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500">年级</label>
            <Select
              value={commonMetadata.grade}
              onValueChange={(v) => updateCommonMetadata({ grade: v })}
              disabled={!commonMetadata.stage}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder={commonMetadata.stage ? "选择年级" : "先选学段"} />
              </SelectTrigger>
              <SelectContent>
                {currentGrades.map((g) => (
                  <SelectItem key={g.code} value={g.code}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 来源类型 */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500">来源类型</label>
            <Select
              value={commonMetadata.source_type}
              onValueChange={(v) => updateCommonMetadata({ source_type: v })}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="来源类型" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="exam">试卷</SelectItem>
                <SelectItem value="exercise">练习册</SelectItem>
                <SelectItem value="textbook">教材</SelectItem>
                <SelectItem value="other">其他</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* 来源名称 */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500">来源名称</label>
            <Input
              className="h-8 text-sm"
              placeholder="例如: 2024期末试卷"
              value={commonMetadata.source_name || ''}
              onChange={(e) => updateCommonMetadata({ source_name: e.target.value })}
            />
          </div>

          {/* 年份 */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500">年份</label>
            <Input
              className="h-8 text-sm"
              type="number"
              placeholder="例如: 2024"
              value={commonMetadata.year || ''}
              onChange={(e) => updateCommonMetadata({ year: e.target.value })}
            />
          </div>

          {/* 地区 */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500">地区</label>
            <Input
              className="h-8 text-sm"
              placeholder="例如: 北京市海淀区"
              value={commonMetadata.region || ''}
              onChange={(e) => updateCommonMetadata({ region: e.target.value })}
            />
          </div>
        </div>

        {/* 考试类型 */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500">考试类型</label>
            <Select
              value={selectValue}
              onValueChange={(v) => updateCommonMetadata({ exam_type: v === 'other' ? 'other' : v })}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="选择考试类型" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>校内考试</SelectLabel>
                  {examTypes.filter(t => !['小升初', '中考', '高考', '真题', '其他'].includes(t.code)).map(t => (
                    <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>
                  ))}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>竞赛</SelectLabel>
                  {competitions.map(c => (
                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectGroup>
                <SelectItem value="other">其他</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isOtherExamType && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-500">其他类型</label>
              <Input
                className="h-8 text-sm"
                placeholder="请输入考试类型"
                value={commonMetadata.exam_type === 'other' ? '' : commonMetadata.exam_type}
                onChange={(e) => updateCommonMetadata({ exam_type: e.target.value })}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
